/**
 * Maps an already-validated receipt from the API onto a form row.
 *
 * The amount and ticket rules live on the server (container-server
 * src/api/receipt/services/etatRules.js), which normalises each ETAT column by
 * its own number format and reconciles every ticket against the subtotal the
 * report prints for itself. This file used to re-implement those rules in
 * TypeScript; the two copies were already diverging, and the frontend copy
 * could only ever be a worse guess because it never sees the column a number
 * came from. It now trusts what the API sends and only reshapes it.
 */

const TICKET_PREFIX = "566";
const TICKET_RE = new RegExp(`${TICKET_PREFIX}\\d{6}`);

export function extractTicketId(value: unknown): string | null {
  const digits = String(value ?? "").replace(/\D/g, "");
  const match = digits.match(TICKET_RE);
  return match ? match[0] : null;
}

/** Display helper: the app shows only the last three digits of a ticket. */
export function toReceiptNumber(value: unknown): string | null {
  const ticket = extractTicketId(value);
  if (ticket) {
    return ticket.slice(-3);
  }
  const digits = String(value ?? "").replace(/\D/g, "");
  if (/^\d{3}$/.test(digits)) {
    return digits;
  }
  return null;
}

/**
 * The API sends the ticket's الوزن total in kg, already cross-checked against
 * the printed subtotal. Accept a number as-is; only fall back to parsing when
 * an older endpoint still sends a string.
 */
export function toAmountReceived(value: unknown): number | null {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  const amount =
    typeof value === "number" ? value : Number(String(value).replace(",", "."));
  if (!Number.isFinite(amount) || amount <= 0) {
    return null;
  }
  return amount;
}

export interface MappedReceipt {
  receipt_number: string | null;
  amount_received: number | null;
  amount_given: number;
  client_id: number | string | null;
  client_name: string | null;
  client_created: boolean;
  needs_review: boolean;
  review_reasons: string[];
}

export function mapParsedReceiptToForm(item: any): MappedReceipt | null {
  const ticket =
    item?.receipt_no ||
    item?.receipt_number ||
    item?.transaction_id ||
    item?.number;
  const receipt_number = toReceiptNumber(ticket);

  if (
    !receipt_number &&
    !item?.needs_review &&
    !item?.client_id &&
    !item?.client_name
  ) {
    return null;
  }

  const reasons: string[] = Array.isArray(item?.review_reasons)
    ? [...item.review_reasons]
    : [];
  if (!receipt_number && !reasons.length) {
    reasons.push("receipt number could not be read");
  }

  return {
    receipt_number,
    amount_received: toAmountReceived(
      item?.amount_received ?? item?.received_amount
    ),
    amount_given: 0,
    client_id:
      item?.client_id != null && item.client_id !== ""
        ? Number(item.client_id) || item.client_id
        : null,
    client_name: item?.client_name ?? null,
    client_created: Boolean(item?.client_created),
    needs_review: Boolean(item?.needs_review) || !receipt_number,
    review_reasons: reasons,
  };
}
